import { createORPCClient } from '@orpc/client'
import { RPCLink } from '@orpc/client/fetch'
import type { RouterClient } from '@orpc/server'
import type { Router } from '../../../api/src/router'
import { supabase } from './supabase'
import { fetchWithTimeout } from './orpc'

const API_URL = import.meta.env.VITE_API_URL
const DEMO_EMAIL = import.meta.env.VITE_DEMO_EMAIL
const DEMO_PASSWORD = import.meta.env.VITE_DEMO_PASSWORD

/** Check if an email has crew records and return associated companies */
export async function checkEmailAccount(email: string): Promise<{ exists: boolean; companies: { id: string; name: string }[] }> {
  const client: RouterClient<Router> = createORPCClient(
    new RPCLink({ url: API_URL, fetch: fetchWithTimeout })
  )
  return await client.auth.checkEmailExists({ email })
}

/** Ask the backend to email a web signup link to the given address. */
export async function sendSignupLink(email: string): Promise<void> {
  const client: RouterClient<Router> = createORPCClient(
    new RPCLink({ url: API_URL, fetch: fetchWithTimeout })
  )
  await client.auth.sendSignupLink({ email })
}

/** True if this email is the App Review demo account. */
export function isDemoAccount(email: string): boolean {
  return Boolean(DEMO_EMAIL) && email === DEMO_EMAIL
}

/** Email a 6-digit sign-in code (the Supabase magic-link template only renders
 *  the {{ .Token }} code — no link). */
export async function sendSignInCode(email: string) {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: false },
  })
  if (error) throw error
}

/** Verify the 6-digit code from the sign-in email. */
export async function verifyEmailOtp(email: string, token: string): Promise<void> {
  const { error } = await supabase.auth.verifyOtp({ email, token, type: 'email' })
  if (error) throw error
}

/** Demo-account password sign-in for App Review (skips magic link). */
export async function signInDemo(email: string) {
  if (!DEMO_EMAIL || !DEMO_PASSWORD || email !== DEMO_EMAIL) {
    throw new Error('Demo sign-in is only available for the review account')
  }
  const { data, error } = await supabase.auth.signInWithPassword({ email, password: DEMO_PASSWORD })
  if (error) throw error
  return data.session
}

/** Check if the authenticated user has a crew record */
export async function checkCrewExists(accessToken: string): Promise<boolean> {
  const client: RouterClient<Router> = createORPCClient(
    new RPCLink({
      url: API_URL,
      fetch: fetchWithTimeout,
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  )
  const crew = await client.crew.getMe()
  return crew !== null
}

/** Sign out */
export async function signOut() {
  await supabase.auth.signOut()
}

/** Get current user */
export async function getCurrentUser() {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  return user
}
